import React, { useEffect, useState } from 'react';
import { ArrowRight, Check } from 'lucide-react';
import api from '../../services/api';
import analytics from '../../services/analytics';

const LeadMagnetSection = ({ content, newsletter }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [magnet, setMagnet] = useState(null);

  // Single source of truth: the backend manifest
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lead-magnet`)
      .then((r) => r.json())
      .then(setMagnet)
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setIsSubmitting(true);
    setError('');
    try {
      await api.subscribeNewsletter(email);
      setIsSuccess(true);
      // GA4 lead-capture conversion
      analytics.trackGenerateLead('lead_magnet_home', { magnet: 'emotion_first_aid_kit' });
      analytics.trackSignUp('newsletter');
      setEmail('');
    } catch (err) {
      const serverDetail = err?.response?.data?.detail;
      setError(
        typeof serverDetail === 'string'
          ? serverDetail
          : 'Something went wrong. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = content?.heading || magnet?.title || 'Emotion First Aid Kit';
  const subtitle = content?.description || magnet?.subtitle || '3 Tools to Help Your Child Through Big Feelings';
  const cta = content?.cta_text || 'Send me the free guide';
  const pageCount = magnet?.pageCount || 5;
  const heroImage = content?.image || magnet?.heroImage || '/images/lead-magnets/emotion-first-aid-kit/page-1.jpg';
  const tools = magnet?.tools || [
    'The 60-second name-it-to-tame-it script',
    'A body-based reset your child can run themselves',
    'The repair conversation for after the storm',
  ];

  return (
    <section className="py-16 lg:py-20 relative overflow-hidden" data-testid="lead-magnet-section">
      {/* Consistent Teal Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0F766E] via-[#0D6B64] to-[#0B5D57]"></div>
      
      {/* Subtle Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/3 translate-y-1/3"></div>
      </div>

      <div className="wrap relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-center">
            
            {/* Left Side: Content (3 columns) */}
            <div className="lg:col-span-3 text-center lg:text-left">
              <p className="text-white/70 text-xs font-semibold uppercase tracking-wider mb-3">
                Free · No purchase required
              </p>
              <h2 className="text-3xl lg:text-[40px] font-bold text-white mb-4" data-testid="lead-magnet-headline">
                {title}
              </h2>
              <p className="text-white/90 text-lg mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                {subtitle} — a {pageCount}-page printable sample from the GoodHabits Family
                collection you can use with your child today.
              </p>

              {/* Real tools from the manifest */}
              <div className="flex flex-col gap-2 mb-8 max-w-md mx-auto lg:mx-0">
                {tools.map((tool, i) => (
                  <div key={tool} className="flex items-start gap-2.5 text-white/95 text-sm">
                    <div className="w-5 h-5 rounded-full bg-white/15 flex-shrink-0 flex items-center justify-center mt-0.5">
                      <Check className="h-3 w-3" />
                    </div>
                    <span><strong>Tool {i + 1}:</strong> {tool}</span>
                  </div>
                ))}
              </div>

              {/* Form */}
              {!isSuccess ? (
                <div>
                  <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto lg:mx-0">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="flex-1 px-5 py-[18px] rounded-full text-[#0B1F3A] placeholder-[#94A3B8] bg-white focus:outline-none focus:ring-2 focus:ring-white/50"
                      required
                      data-testid="lead-magnet-email-input"
                    />
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="inline-flex items-center justify-center gap-2 bg-[#0B1F3A] hover:bg-[#0B1F3A]/90 text-white font-semibold px-6 py-[18px] rounded-full transition-colors disabled:opacity-70"
                      data-testid="lead-magnet-submit-btn"
                    >
                      {isSubmitting ? 'Sending...' : cta}
                      <ArrowRight className="h-5 w-5" />
                    </button>
                  </form>
                  {error && <p className="text-red-200 text-sm mt-3">{error}</p>}
                  <p className="text-white/60 text-sm mt-4">
                    Trusted by parents looking for calmer, more connected homes.
                  </p>
                </div>
              ) : (
                <div className="bg-white/10 backdrop-blur rounded-2xl p-6 max-w-md mx-auto lg:mx-0 border border-white/10">
                  <div className="flex items-center justify-center lg:justify-start gap-3 text-white">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <Check className="h-5 w-5" />
                    </div>
                    <span className="font-semibold">Check your inbox for instant access!</span>
                  </div>
                </div>
              )}
            </div>

            {/* Right Side: REAL PDF cover (from manifest, generated from source PDF) */}
            <div className="lg:col-span-2 flex justify-center lg:justify-end">
              <div className="relative">
                <div className="relative w-56 lg:w-64 transform hover:scale-105 transition-transform duration-300">
                  {/* Soft glow */}
                  <div className="absolute inset-0 bg-white/20 rounded-xl blur-2xl transform scale-110"></div>
                  {/* Drop shadow */}
                  <div className="absolute inset-0 bg-black/25 rounded-lg blur-xl transform translate-y-4 translate-x-2"></div>
                  {/* Real cover from manifest */}
                  <div className="relative bg-white rounded-lg shadow-2xl overflow-hidden">
                    <img
                      src={heroImage}
                      alt={`${title} — page 1`}
                      className="w-full aspect-[3/4] object-cover object-top"
                      data-testid="lead-magnet-hero-image"
                    />
                  </div>
                  {/* Page stack effect */}
                  <div className="absolute -bottom-1 -right-1 w-full h-full bg-white/50 rounded-lg -z-10 transform rotate-2"></div>
                  <div className="absolute -bottom-2 -right-2 w-full h-full bg-white/30 rounded-lg -z-20 transform rotate-4"></div>
                </div>
                
                {/* Floating badge */}
                <div className="absolute -top-3 -right-3 bg-[#F59E0B] text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                  FREE · {pageCount}pp
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LeadMagnetSection;
